import React from 'react';
import { Alert } from 'bumbag';

import { ErrorMessage } from '@medipass/react-core';
import type { PayPalPaymentToken } from '@medipass/web-sdk';
import sdk from '@medipass/web-sdk';

import { createPayPalFlow } from './flow';
import { loadPayPalSDK } from './loadSDK';
import { MessageStatus, renderPayPalMessage } from './renderMessage';
import type { PayPalButtonsInstance, PayPalCheckoutConfig } from './types';

type Props = {
  config: PayPalCheckoutConfig;
  active?: boolean;
  validate: () => Promise<boolean>;
  onApprove: (token: PayPalPaymentToken) => Promise<void>;
  onBusyChange: (busy: boolean) => void;
};

export default function PayPalButtons({ config, active = true, ...callbacks }: Props) {
  const paypalContainer = React.useRef<HTMLDivElement>(null);
  const payLaterContainer = React.useRef<HTMLDivElement>(null);
  const messagesContainer = React.useRef<HTMLDivElement>(null);
  const handlers = React.useRef({ ...callbacks, active });
  handlers.current = { ...callbacks, active };
  const [error, setError] = React.useState<unknown>();
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState('loading');
  const [messageStatus, setMessageStatus] = React.useState<MessageStatus>('loading');
  const [buttonCount, setButtonCount] = React.useState(2);
  const [attempt, setAttempt] = React.useState(0);
  const { accountId, clientId, currencyCode, amount, invoiceId, sessionId, businessId, expiresAt } = config;

  React.useEffect(() => {
    let disposed = false;
    let disposeMessage: (() => void) | undefined;
    const instances: PayPalButtonsInstance[] = [];
    const assertNotExpired = () => {
      if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
        throw new Error('This payment request has expired. Please contact your provider.');
      }
    };
    const flow = createPayPalFlow({
      validate: async () => {
        if (!handlers.current.active) return false;
        assertNotExpired();
        setError(undefined);
        return handlers.current.validate();
      },
      initiate: () => {
        if (!handlers.current.active) throw new Error('Please select PayPal before continuing.');
        assertNotExpired();
        return sdk.sales.initiateBrowserPayment(
          businessId,
          { amount, currencyCode, paymentType: 'paypal' },
          {
            requestConfig: { tokenType: 'session' },
          },
        );
      },
      approve: token => {
        if (!handlers.current.active) throw new Error('Please select PayPal before continuing.');
        return handlers.current.onApprove(token);
      },
      onBusyChange: busy => {
        if (!disposed) setBusy(busy);
        handlers.current.onBusyChange(busy);
      },
      onError: setError,
      onCancel: () => setError(undefined),
    });
    setError(undefined);
    setStatus('loading');
    setMessageStatus('loading');
    setButtonCount(2);
    const render = async () => {
      const paypal = await loadPayPalSDK({ accountId, clientId, currencyCode });
      if (disposed) return;
      const eligibleButtons = [];
      for (const [fundingSource, container] of [
        [paypal.FUNDING.PAYPAL, paypalContainer.current],
        [paypal.FUNDING.PAYLATER, payLaterContainer.current],
      ] as const) {
        if (!container) continue;
        const buttons = paypal.Buttons({
          ...flow,
          fundingSource,
          style: { layout: 'vertical', color: 'gold', shape: 'rect', height: 48 },
        });
        instances.push(buttons);
        if (buttons.isEligible()) eligibleButtons.push({ buttons, container });
        else if (fundingSource === paypal.FUNDING.PAYPAL)
          throw new Error('PayPal is unavailable. Please choose another payment method.');
      }
      setButtonCount(eligibleButtons.length);
      if (messagesContainer.current) {
        // Messages render independently so slow or unavailable messaging cannot block payment.
        disposeMessage = renderPayPalMessage(paypal, messagesContainer.current, amount, setMessageStatus);
      }
      await Promise.all(eligibleButtons.map(({ buttons, container }) => buttons.render(container)));
      if (!disposed) setStatus('ready');
    };
    render().catch(error => {
      if (disposed) return;
      setStatus('failed');
      flow.onError(error);
    });
    return () => {
      disposed = true;
      disposeMessage?.();
      flow.dispose();
      instances.forEach(instance => {
        void instance.close().catch(() => undefined);
      });
    };
  }, [accountId, clientId, currencyCode, amount, invoiceId, sessionId, businessId, expiresAt, attempt]);

  return (
    <div className="space-y-2" aria-label="PayPal checkout">
      {busy && <div role="status">PayPal payment in progress…</div>}

      {error != null && (
        <Alert role="alert" type="danger" variant="tint">
          <ErrorMessage
            description="Unable to complete your PayPal payment. Please try again or choose another payment method."
            err={error as Record<string, any>}
          />
        </Alert>
      )}
      {status === 'failed' && (
        <button type="button" className="underline" onClick={() => setAttempt(value => value + 1)}>
          Reload PayPal
        </button>
      )}
      <div hidden={status === 'failed'}>
        <div
          ref={messagesContainer}
          data-testid="paypal-message"
          aria-hidden={messageStatus !== 'ready'}
          {...(messageStatus !== 'ready' ? { inert: '' } : {})}
          style={messageStatus === 'ready' ? { marginBottom: 8 } : { height: 0, overflow: 'hidden', opacity: 0 }}
        />
        <div className="relative" aria-busy={status === 'loading'}>
          {status === 'loading' && (
            <div role="status" aria-label="Loading PayPal payment buttons" className="absolute inset-0 space-y-2">
              {Array.from({ length: buttonCount }, (_, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="h-12 rounded bg-neutral-darkest motion-safe:animate-pulse"
                />
              ))}
            </div>
          )}
          <div
            className="space-y-2"
            aria-hidden={status !== 'ready'}
            {...(status !== 'ready' ? { inert: '' } : {})}
            style={{
              minHeight: buttonCount * 48 + (buttonCount - 1) * 8,
              opacity: status === 'ready' ? 1 : 0,
            }}
          >
            <div ref={paypalContainer} />
            <div ref={payLaterContainer} />
          </div>
        </div>
      </div>
    </div>
  );
}
